import type MediaServicesSearchClient from '@foundation/media-services-search-client/lib/index';

import { Author, Company, Person, SearchResult, SearchResults } from '../types';

const LOOKUP_TYPES_SIZE = [3, 3, 3, 3, 3, 3, 3, 3, 6];
const LOOKUP_FIELDS = [
  'name',
  'slug',
  'ticker_symbol',
  'url',
  'organization',
  'title',
  'primary_site',
];
const LOOKUP_FINANCE_TYPES = [
  'Company_Public',
  'Index',
  'Fund',
  'Currency',
  'Commodity',
  'Bond',
];
const LOOKUP_PERSON_TYPES = ['Person', 'Author'];
const LOOKUP_FUZZINESS_DEFAULT_LEVEL = 'AUTO';

const AUTHOR_URL_MAP: Record<string, string> = {
  bpol: 'http://www.bloomberg.com/authors/',
  bbiz: 'http://www.bloomberg.com/authors/',
  bview: 'https://www.bloomberg.com/view/contributors/',
  gadfly: 'https://www.bloomberg.com/gadfly/columnists/',
  default: 'http://www.bloomberg.com/authors/',
};

type SearchClientLookupResult = {
  group: string;
  results: Array<Company | Person>;
};

type AutocompleteLookupResult = {
  autocomplete: Array<string>;
};

export const getAutocompleteResults = async (
  query: string,
  searchClient: MediaServicesSearchClient
): Promise<{ autocomplete: SearchResult[] }> => {
  if (!query) {
    return { autocomplete: [] };
  }

  const results = (await searchClient.autoComplete({
    query: query.split(' ').join('+'),
  })) as AutocompleteLookupResult;

  const autocomplete = (results.autocomplete || []).map((result) => {
    return {
      url: `/search?query=${result}`,
      text: result,
      id: result,
    };
  });

  return {
    autocomplete: autocomplete.slice(0, 3),
  };
};

const transformSecurities = (map: Map<string, Array<Company>>) => {
  const tickers = new Set();

  return LOOKUP_FINANCE_TYPES.flatMap(
    (type) => (map.get(type) as Company[]) || []
  )
    .filter(({ ticker_symbol }) => {
      if (tickers.has(ticker_symbol)) return false;

      tickers.add(ticker_symbol);

      return true;
    })
    .sort((a, b) => b.score - a.score)
    .map(({ ticker_symbol, url }) => {
      return {
        id: ticker_symbol,
        text: ticker_symbol,
        url,
      };
    })
    .slice(0, 3);
};

const transformAuthors = (map: Map<string, Array<Author>>) => {
  return (map.get('Author') || []).map(
    ({ name, primary_site, slug, score }) => {
      const site = AUTHOR_URL_MAP[primary_site] ? primary_site : 'default';

      return {
        name: `Author: ${name}`,
        url: `${AUTHOR_URL_MAP[site]}${slug}`,
        slug,
        score,
        primary_site,
      } as Person;
    }
  );
};

const transformPersonsWithAuthors = (
  map: Map<string, Array<Person>>,
  authors: Person[]
) => {
  return (map.get('Person') || [])
    .concat(authors)
    .sort((a, b) => b.score - a.score)
    .map(({ name, url }) => {
      return {
        id: url,
        text: name,
        url,
      };
    })
    .slice(0, 3);
};

export const getLookupResults = async (
  query: string,
  searchClient: MediaServicesSearchClient,
  countryCode?: string
): Promise<{ securities: SearchResult[]; people: SearchResult[] }> => {
  if (!query) {
    return { securities: [], people: [] };
  }

  const results =
    ((await searchClient.lookup({
      query,
      types: LOOKUP_FINANCE_TYPES.concat(LOOKUP_PERSON_TYPES),
      excludeSubtypes: ['label:editorial'],
      groupSize: LOOKUP_TYPES_SIZE,
      country: countryCode,
      fields: LOOKUP_FIELDS,
      boostParents: true,
      fuzziness: LOOKUP_FUZZINESS_DEFAULT_LEVEL,
    })) as Array<SearchClientLookupResult>) || [];

  const map = results.reduce((acc, result) => {
    return acc.set(result.group, result.results || []);
  }, new Map());

  const securities = transformSecurities(map);
  const authors = transformAuthors(map);
  const people = transformPersonsWithAuthors(map, authors);

  return {
    securities,
    people,
  };
};

export const getSearchResults = async (
  query: string,
  searchClient: MediaServicesSearchClient,
  countryCode?: string
): Promise<SearchResults> => {
  const [lookupResults, autocompleteResults] = await Promise.all([
    getLookupResults(query.trim(), searchClient, countryCode),
    getAutocompleteResults(query.trim(), searchClient),
  ]);

  return {
    suggestions: {
      id: 'suggestions',
      text: 'Suggestions',
      dropdownListItems: autocompleteResults.autocomplete,
    },
    securities: {
      id: 'securities',
      text: 'Securities',
      dropdownListItems: lookupResults.securities,
    },
    people: {
      id: 'people',
      text: 'People',
      dropdownListItems: lookupResults.people,
    },
  };
};
